import React, { useEffect, useState } from 'react';
import { FaTrash, FaCheck, FaFilePdf } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { berAPI, tenderAPI } from '../../../utils/api';
import DataTable from '../../../components_shared/DataTable';
import TableToolbar from '../../../components_shared/TableToolbar';

export default function BerUploadPage() {
  const [rows, setRows] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [berRes, tenderRes] = await Promise.all([berAPI.getAll(), tenderAPI.getAll()]);
      setRows(Array.isArray(berRes.data) ? berRes.data : (berRes.data.content || []));
      setTenders(Array.isArray(tenderRes.data) ? tenderRes.data : (tenderRes.data.content || []));
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    const ok = await Swal.fire({ title: 'Delete BER Report?', text: 'This will permanently remove the report.', icon: 'warning', showCancelButton: true });
    if (ok.isConfirmed) {
      try {
        await berAPI.delete(id);
        load();
        Swal.fire({ title: 'Deleted', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
      } catch (err) { Swal.fire('Error', err.message, 'error'); }
    }
  };

  const authorize = async id => { 
    await berAPI.authorize(id); 
    await load(); 
    Swal.fire({ title: 'Authorized', text: 'BER Report has been approved', icon: 'success' });
  };

  const handleUpload = async () => {
    if (tenders.length === 0) {
      return Swal.fire('Error', 'No active tenders available to upload BER for.', 'error');
    }

    const opts = tenders.map(t => `<option value="${t.id}">${t.ref_no || t.refNo}</option>`).join('');
    const { value: tenderId } = await Swal.fire({
      title: 'Select Tender',
      html: `<select id="tender_select" class="swal2-input" style="width: 80%">${opts}</select>`,
      confirmButtonText: 'Next',
      showCancelButton: true,
      preConfirm: () => document.getElementById('tender_select').value
    });

    if (tenderId) {
      const { value: file } = await Swal.fire({
        title: 'Select BER File (PDF)',
        input: 'file',
        inputAttributes: { accept: 'application/pdf', 'aria-label': 'Upload your BER report' },
        showCancelButton: true
      });

      if (file) {
        setLoading(true);
        try {
          await berAPI.upload(tenderId, file);
          await load();
          Swal.fire('Success', 'BER report uploaded successfully', 'success');
        } catch (err) {
          Swal.fire('Error', err.message, 'error');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { 
      accessorKey: 'tender.ref_no', 
      header: 'Tender Reference', 
      cell: ({ row }) => <span style={{ fontWeight: 600 }}>{row.original.tender?.ref_no || row.original.tender?.refNo || `#${row.original.tender_id}`}</span>
    },
    { 
      accessorKey: 'file_path', 
      header: 'Report File', 
      cell: ({ getValue }) => (
        <a href={getValue()} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e74c3c', fontWeight: 600 }}>
          <FaFilePdf /> Download PDF
        </a>
      )
    },
    { 
      accessorKey: 'status', 
      header: 'Auth Status',
      cell: ({ getValue }) => (
        <span className={`eduss-global-status-badge ${getValue() === 'AUTHORIZED' ? 'active' : 'pending'}`}>
          {getValue()}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          {row.original.status === 'UPLOADED' && (
            <button className="eduss-global-action-btn active" style={{ background: 'rgba(16, 172, 132, 0.1)', color: '#10ac84' }} onClick={() => authorize(row.original.id)} title="Authorize"><FaCheck /></button>
          )}
          <button className="eduss-global-action-btn delete" onClick={() => remove(row.original.id)}><FaTrash /></button>
        </div>
      ),
    },
  ];

  const filteredData = rows.filter(r => 
    !filter || r.tender?.ref_no?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar 
        title="BER Reports"
        subtitle="Bid Evaluation Reports and technical qualification approvals"
        onAdd={handleUpload}
        onSearch={setFilter}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
          enableSelection={true}
        />
      </div>
    </div>
  );
}
